import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch } from '../../../store';
import { IPage } from '../../../models/page';
import useTranslations from '../../../hooks/useTranslations';
import { FieldTypes, IFormFields } from '../../../models/form';
import Form from '../../../components/Container/Form/Form';
import Alert from '../../../components/UI/Alert/Alert';
import BackButton from '../../../components/UI/BackButton/BackButton';
import { CustomerAssignmentsActions } from '../../../store/entities/customerAssignments/customerAssignments.actions';
import { VouchersActions } from '../../../store/entities/vouchers/vouchers.actions';
import { CONSTANTS } from '../../../utils/constants';
import usePermissionRedirect from '../../../hooks/usePermissionRedirect';
import useSiteOptions from '../../../hooks/useSiteOptions';
import { useAppSelector } from '../../../store';
import { LockersActions } from '../../../store/entities/lockers/lockers.actions';
import { now } from 'lodash';
import { customerAssignmentDetailsValidation } from '../../../utils/formValidation/customerAssignmentDetails';
import { getCustomerVouchers } from '../voucherHelpers';
import { SiteTypes } from '../../../models/pricingPlan';

const CustomerAssignmentDetails = ({ permission, nextRoute }: IPage) => {
  const [show, setShow] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>('');
  const siteList = useSiteOptions(SiteTypes.residential);
  const [loadedAssignment, setLoadedAssignment] = useState<any | null>(null);
  const [loadedProVoucher, setLoadedProVoucher] = useState<any | null>(null);
  const [loadedMailBoxVoucher, setLoadedMailBoxVoucher] = useState<any | null>(
    null
  );

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { rcTranslate } = useTranslations();
  usePermissionRedirect(permission, nextRoute);

  const { phoneNumber = '' } = useParams();
  const isEdit = !!phoneNumber;

  const { vouchers } = useAppSelector((state) => state.vouchers);
  const { customerAssignments } = useAppSelector(
    (state) => state.customerAssignments
  );
  const { lockers } = useAppSelector((state) => state.lockers);

  // Load customer assignment and vouchers on edit
  useEffect(() => {
    const loadData = async () => {
      if (!isEdit) return;

      // Load customer assignments
      await dispatch(
        CustomerAssignmentsActions.getCustomerAssignments({
          customerPhoneNumber: phoneNumber,
        })
      );

      // Load vouchers
      await dispatch(
        VouchersActions.getVouchers({ customerPhoneNumber: phoneNumber })
      );
    };
    loadData();
  }, [dispatch, isEdit, phoneNumber]);

  // Set loaded data when available
  useEffect(() => {
    if (!isEdit) return;

    const assignment = customerAssignments?.find(
      (a) => a.phoneNumber === phoneNumber
    );

    if (assignment) {
      setLoadedAssignment(assignment);

      // Find matching vouchers
      const { proVoucher, mailBoxVoucher } = getCustomerVouchers(
        vouchers,
        assignment.siteId,
        phoneNumber
      );

      setLoadedProVoucher(proVoucher || null);
      setLoadedMailBoxVoucher(mailBoxVoucher || null);

      // Load cells for the site
      if (assignment?.siteId) {
        dispatch(
          LockersActions.getLockers({ SiteId: assignment.siteId }, null, false)
        );
      }
    }
  }, [isEdit, phoneNumber, vouchers, customerAssignments, dispatch]);

  // Compute mailbox numbers from cells
  const mailBoxNumbersList = useMemo(() => {
    if (!lockers || !Array.isArray(lockers) || lockers.length === 0) {
      return [];
    }

    const mailBoxes: { id: string; label: string }[] = [];

    lockers.forEach((cell: any) => {
      if (cell.isMailBox && cell.mailBoxNumber) {
        mailBoxes.push({
          id: cell.mailBoxNumber,
          label: cell.mailBoxNumber,
        });
      }
    });
    return mailBoxes.sort((a, b) => a.label.localeCompare(b.label));
  }, [lockers]);

  const fields: IFormFields = useMemo(() => {
    return {
      initialValues: {
        fullNameFromContractor: loadedAssignment?.fullNameFromContractor ?? '',
        siteId: loadedAssignment?.siteId ?? '',
        lockerNumber: loadedAssignment?.lockerNumber ?? '',
        voucherExpiry:
          loadedProVoucher?.expiryDate ??
          loadedMailBoxVoucher?.expiryDate ??
          '',
        proDuration: loadedProVoucher?.durationInMonths ?? null,
        mailBoxDuration: loadedMailBoxVoucher?.durationInMonths ?? null,
      },
      formData: [
        {
          type: FieldTypes.TEXT,
          id: 'fullNameFromContractor',
          label: 'customerAssignments.edit.fullNameFromContractor',
        },
        {
          type: FieldTypes.AUTOCOMPLETE,
          id: 'siteId',
          label: 'customerAssignments.filters.siteId',
          items: siteList,
          props: { multiple: false },
        },
        {
          type: FieldTypes.AUTOCOMPLETE,
          id: 'lockerNumber',
          label: 'customerAssignments.filters.mailBoxNumber',
          items: mailBoxNumbersList,
          props: { multiple: false },
        },
        {
          type: FieldTypes.DATE,
          id: 'voucherExpiry',
          label: 'customerAssignments.edit.voucherExpiry',
          props: {
            getFullDayTime: true,
            numOfDays: 1,
            minDate: new Date(now()),
          },
        },
        {
          type: FieldTypes.NUMBER,
          id: 'proDuration',
          label: 'customerAssignments.edit.proDuration',
        },
        {
          type: FieldTypes.NUMBER,
          id: 'mailBoxDuration',
          label: 'customerAssignments.edit.mailBoxDuration',
        },
      ],
    } as IFormFields;
  }, [
    loadedAssignment,
    loadedProVoucher,
    loadedMailBoxVoucher,
    siteList,
    mailBoxNumbersList,
  ]);

  const onSubmit = async (values: any, setSubmitting: (v: boolean) => void) => {
    if (!phoneNumber) {
      setSubmitting(false);
      return;
    }

    // Update customer assignment
    const result = await dispatch(
      CustomerAssignmentsActions.updateCustomerAssignment(phoneNumber, {
        siteId: values.siteId,
        lockerNumber: values.lockerNumber || null,
        fullNameFromContractor: values.fullNameFromContractor || null,
        voucherExpiryDate: values.voucherExpiry || null,
        sbSubscriptionVoucherDurationInMonths: values.proDuration || null,
        upgradeToSmartLockerVoucherDurationInMonths:
          values.mailBoxDuration || null,
      })
    );

    setSubmitting(false);

    if (result !== undefined) {
      setAlertMessage(rcTranslate('customerAssignments.edit.updateSuccess'));
      setShow(true);

      setTimeout(() => {
        setShow(false);
        navigate(
          '/' +
            CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.BASE +
            '/' +
            CONSTANTS.ROUTES.CUSTOMER_ASSIGNMENTS_MANAGEMENT.CHILDREN
              .CUSTOMER_ASSIGNMENTS_LIST
        );
      }, 3000);
    }
  };

  return (
    <>
      <BackButton />

      <Form
        fields={fields}
        formValidation={customerAssignmentDetailsValidation}
        onSubmit={onSubmit}
        submitButtonTitle={rcTranslate('customerAssignments.edit.updateButton')}
        entityIdentifier={{
          label: 'customerAssignments.edit.entityIdentifier',
          id: phoneNumber,
        }}
      />
      {show && <Alert message={alertMessage} color="success" />}
    </>
  );
};

export default CustomerAssignmentDetails;
